// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/hooks/useFiscal.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cbenefService, cestService,
  cfopService, cstService, naturezaOperacaoService, apoioService,
  regraFiscalService, cClassTribService, correlacaoService,
  monofasicoService, tabUfIcmsService,
  anpService, fcpService, ncmService,
  classificacaoFiscalService,
} from '../services/fiscalService';
import type {
  SalvarCBenefRequest,
  SalvarCestRequest,
  SalvarCfopRequest,
  SalvarNaturezaOperacaoRequest,
  SalvarRegraFiscalRequest,
  SalvarCClassTribRequest,
  SalvarCorrelacaoCfopCstRequest,
  SalvarMonofasicoRequest,
  SalvarTabUfIcmsRequest,
  SalvarCstIcmsRequest,
  SalvarCsosnRequest,
  SalvarAnpRequest,
  SalvarNcmIcmsPartilhaRequest,
  SalvarNcmRequest,
  SalvarClassificacaoFiscalRequest,
} from '../types';

// ── CEST ──────────────────────────────────────────────────────────────────

const CEST_KEY = 'cest';

export function useCest(params?: { busca?: string; segmento?: string; pagina?: number; tamanhoPagina?: number }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CEST_KEY, params],
    queryFn: () => cestService.listar(params),
  });

  const obterPorId = (id: number) => cestService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCestRequest) => cestService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CEST_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCestRequest }) =>
      cestService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CEST_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cestService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CEST_KEY] }),
  });

  const importar = useMutation({
    mutationFn: (arquivo: File) => cestService.importar(arquivo),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CEST_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir, importar };
}

// ── CBenef ────────────────────────────────────────────────────────────────

const CBENEF_KEY = 'cbenef';

export function useCBenef(params?: { busca?: string; produtoId?: number; uf?: string; pagina?: number; tamanhoPagina?: number }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CBENEF_KEY, params],
    queryFn: () => cbenefService.listar(params),
  });

  const obterPorId = (id: number) => cbenefService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCBenefRequest) => cbenefService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CBENEF_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCBenefRequest }) =>
      cbenefService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CBENEF_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cbenefService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CBENEF_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── CFOP ──────────────────────────────────────────────────────────────────

const CFOP_KEY = 'cfops';

export function useCfops(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CFOP_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => cfopService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => cfopService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCfopRequest) => cfopService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CFOP_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCfopRequest }) =>
      cfopService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CFOP_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cfopService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CFOP_KEY] }),
  });

  const importar = useMutation({
    mutationFn: (arquivo: File) => cfopService.importar(arquivo),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CFOP_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir, importar };
}

export function useCfopsCombo(busca?: string) {
  return useQuery({
    queryKey: ['cfops-combo', busca],
    queryFn: () => cfopService.combo(busca),
    staleTime: 5 * 60 * 1000,
  });
}

// ── CST ───────────────────────────────────────────────────────────────────

const CST_ICMS_KEY = 'cst-icms';

export function useCstIcmsCrud(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CST_ICMS_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => cstService.listarIcms(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => cstService.obterIcmsPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCstIcmsRequest) => cstService.criarIcms(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CST_ICMS_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCstIcmsRequest }) =>
      cstService.atualizarIcms(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CST_ICMS_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cstService.excluirIcms(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CST_ICMS_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

/** @deprecated Use useCstIcmsCrud */
export function useCstIcms(busca?: string) {
  return useQuery({
    queryKey: [CST_ICMS_KEY, busca],
    queryFn: () => cstService.listarIcms(busca),
  });
}

export function useCstIcmsCombo() {
  return useQuery({
    queryKey: ['cst-icms-combo'],
    queryFn: cstService.comboIcms,
    staleTime: 10 * 60 * 1000,
  });
}

const CSOSN_KEY = 'csosn';

export function useCsosnCrud(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CSOSN_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => cstService.listarCsosn(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => cstService.obterCsosnPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCsosnRequest) => cstService.criarCsosn(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CSOSN_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCsosnRequest }) =>
      cstService.atualizarCsosn(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CSOSN_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cstService.excluirCsosn(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CSOSN_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

export function useCsosnCombo() {
  return useQuery({
    queryKey: ['csosn-combo'],
    queryFn: cstService.comboCsosn,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCstPisCofinsCombo() {
  return useQuery({
    queryKey: ['cst-piscofins-combo'],
    queryFn: cstService.comboPisCofins,
    staleTime: 10 * 60 * 1000,
  });
}

export function useOrigensCombo() {
  return useQuery({ queryKey: ['origens-combo'], queryFn: apoioService.origens, staleTime: 10 * 60 * 1000 });
}

export function useCstsIpiCombo() {
  return useQuery({ queryKey: ['csts-ipi-combo'], queryFn: apoioService.cstsIpi, staleTime: 10 * 60 * 1000 });
}

export function useNaturezasReceitaCombo(busca?: string) {
  return useQuery({ queryKey: ['naturezas-receita-combo', busca], queryFn: () => apoioService.naturezasReceita(busca), staleTime: 10 * 60 * 1000 });
}

// ── ANP ───────────────────────────────────────────────────────────────────

const ANP_KEY = 'anp';

export function useAnp(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [ANP_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => anpService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => anpService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarAnpRequest) => anpService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ANP_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarAnpRequest }) =>
      anpService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ANP_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => anpService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ANP_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── FCP — NcmIcmsPartilha ─────────────────────────────────────────────────

const FCP_KEY = 'fcp';

export function useFcp(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [FCP_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => fcpService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => fcpService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarNcmIcmsPartilhaRequest) => fcpService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FCP_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarNcmIcmsPartilhaRequest }) =>
      fcpService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FCP_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => fcpService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FCP_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── NCM ───────────────────────────────────────────────────────────────────

const NCM_KEY = 'ncm';

export function useNcm(busca?: string, ativo?: boolean | null, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [NCM_KEY, busca, ativo, pagina, tamanhoPagina],
    queryFn: () => ncmService.listar(busca, ativo, pagina, tamanhoPagina),
  });

  const obterPorCodigo = (codigo: string) => ncmService.obterPorCodigo(codigo);

  const criar = useMutation({
    mutationFn: (req: SalvarNcmRequest) => ncmService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NCM_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ codigo, req }: { codigo: string; req: SalvarNcmRequest }) =>
      ncmService.atualizar(codigo, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NCM_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (codigo: string) => ncmService.excluir(codigo),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NCM_KEY] }),
  });

  const importar = useMutation({
    mutationFn: (arquivo: File) => ncmService.importar(arquivo),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NCM_KEY] }),
  });

  return { query, obterPorCodigo, criar, atualizar, excluir, importar };
}

// ── Natureza de Operação ──────────────────────────────────────────────────

const NATUREZA_KEY = 'naturezas-operacao';

export function useNaturezasOperacao(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [NATUREZA_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => naturezaOperacaoService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => naturezaOperacaoService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarNaturezaOperacaoRequest) => naturezaOperacaoService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NATUREZA_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarNaturezaOperacaoRequest }) =>
      naturezaOperacaoService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NATUREZA_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => naturezaOperacaoService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NATUREZA_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

export function useNaturezasOperacaoCombo() {
  return useQuery({
    queryKey: [NATUREZA_KEY, 'combo'],
    queryFn: naturezaOperacaoService.combo,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Regra Fiscal ──────────────────────────────────────────────────────────

const REGRA_KEY = 'regras-fiscais';

export function useRegrasFiscais(params?: { ncmId?: number; cfopId?: number; ufDestino?: string; busca?: string; pagina?: number; tamanhoPagina?: number }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [REGRA_KEY, params],
    queryFn: () => regraFiscalService.listar(params),
  });

  const obterPorId = (id: number) => regraFiscalService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarRegraFiscalRequest) => regraFiscalService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [REGRA_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarRegraFiscalRequest }) =>
      regraFiscalService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [REGRA_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => regraFiscalService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [REGRA_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── CClass Tributária ─────────────────────────────────────────────────────

const CCLASS_KEY = 'cclass-trib';

export function useCClassTrib(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CCLASS_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => cClassTribService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => cClassTribService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCClassTribRequest) => cClassTribService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CCLASS_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCClassTribRequest }) =>
      cClassTribService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CCLASS_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => cClassTribService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CCLASS_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

export function useCClassTribCombo() {
  return useQuery({
    queryKey: [CCLASS_KEY, 'combo'],
    queryFn: cClassTribService.combo,
    staleTime: 5 * 60_000,
  });
}

// ── Correlação CFOP/CST ───────────────────────────────────────────────────

const CORRELACAO_KEY = 'correlacoes-cfop-cst';

export function useCorrelacoesCfopCst(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CORRELACAO_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => correlacaoService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => correlacaoService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarCorrelacaoCfopCstRequest) => correlacaoService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CORRELACAO_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarCorrelacaoCfopCstRequest }) =>
      correlacaoService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CORRELACAO_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => correlacaoService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CORRELACAO_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── Monofásico PIS/COFINS ─────────────────────────────────────────────────

const MONOFASICO_KEY = 'monofasicos';

export function useMonofasicos(busca?: string, pagina = 1, tamanhoPagina = 20) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [MONOFASICO_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => monofasicoService.listar(busca, pagina, tamanhoPagina),
  });

  const obterPorId = (id: number) => monofasicoService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarMonofasicoRequest) => monofasicoService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [MONOFASICO_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarMonofasicoRequest }) =>
      monofasicoService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [MONOFASICO_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => monofasicoService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [MONOFASICO_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── Classificação Fiscal ──────────────────────────────────────────────────

const CLAS_FISCAL_KEY = 'classificacoes-fiscais';

export function useClassificacoesFiscais(params?: { ncmId?: number; busca?: string; pagina?: number; tamanhoPagina?: number }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [CLAS_FISCAL_KEY, params],
    queryFn: () => classificacaoFiscalService.listar(params),
  });

  const obterPorId = (id: number) => classificacaoFiscalService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarClassificacaoFiscalRequest) => classificacaoFiscalService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CLAS_FISCAL_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarClassificacaoFiscalRequest }) =>
      classificacaoFiscalService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CLAS_FISCAL_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => classificacaoFiscalService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CLAS_FISCAL_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

// ── Tabela UF ICMS ────────────────────────────────────────────────────────

const UF_ICMS_KEY = 'uf-icms';

export function useTabUfIcms() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [UF_ICMS_KEY],
    queryFn: () => tabUfIcmsService.listar(),
    staleTime: 5 * 60 * 1000,
  });

  const criar = useMutation({
    mutationFn: ({ ufOrigem, req }: { ufOrigem: string; req: SalvarTabUfIcmsRequest }) =>
      tabUfIcmsService.criar(ufOrigem, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [UF_ICMS_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ ufOrigem, req }: { ufOrigem: string; req: SalvarTabUfIcmsRequest }) =>
      tabUfIcmsService.atualizar(ufOrigem, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [UF_ICMS_KEY] }),
  });

  return { query, criar, atualizar };
}
